import { print as printGraphql } from 'graphql';
import { withGroupPayments } from '~/shared/helpers/group-payment-document';
const print = (document) => printGraphql(withGroupPayments(document));
import type { GroupCourse } from '~/shared/libs/course-learning/context';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import createOneUserGroupMutation from '~/shared/graphql-queries/user-groups/create-one-user-group-mutation';
import findOneUserGroupQuery from '~/shared/graphql-queries/user-groups/find-one-user-group-query';
import removeByIdUserGroupMutation from '~/shared/graphql-queries/user-groups/remove-by-id-user-group-mutation';
import updateByIdUserGroupMutation from '~/shared/graphql-queries/user-groups/update-by-id-user-group-mutation';
import updateStudentLevelUserGroupMutation from '~/shared/graphql-queries/user-groups/update-student-level-user-group-mutation';
import userGroupFragment from '~/shared/graphql-queries/user-groups/user-group-fragment';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationUserGroupResponse = Readonly<{
  data?: {
    paginationUserGroupQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationUserGroupResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationUserGroupArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type RemoveByIdUserGroupArgs = Readonly<{
  _id?: string;
}>;

type FindOneUserGroupArgs = Readonly<{
  filter?: Record<string, unknown>;
}>;

type UpdateByIdUserGroupArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
  hideFlashMessage?: boolean;
}>;

type UpdateStudentLevelUserGroupArgs = Readonly<{
  groupId?: string;
  courseCategoryId?: string;
}>;

type CreateOneUserGroupArgs = Readonly<{
  type?: string;
  name?: string;
  teacher?: string;
  course?: string;
  courseCategory?: string;
  users?: unknown[];
  memberPayments?: unknown[];
}>;

const transformUserGroupResponse =
  (type: 'paginationUserGroupQuery') =>
  (response: PaginationUserGroupResponse): PaginationUserGroupResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const userGroupsTagTypes = ['userGroups'] as const;

export const getUserGroupsQuery = (builder: BuilderType) => {
  return {
    groupLearningView: builder.query<any, { filter: Record<string, unknown> }>({
      query: ({ filter }) => ({ url: '', method: 'POST', body: JSON.stringify({
        query: `query GroupLearningView($filter: FilterFindOneUserGroupInput) {
          findOneUserGroupQuery(filter: $filter) {
            _id name isRemoved users { user { _id } paymentTransactions {
              _id date source providerPaymentId status amount paidAmount debt lessons currency
            } remainingPaidLessons totalPaidAmount }
            data { course { _id } courseCategory { _id } lessonTemplate { _id title }
              completeCourseCategories { _id lessonTemplate }
              teacher { _id firstName lastName role company } }
          }
        }`, variables: { filter },
      }) }),
      providesTags: ['userGroups'],
      transformResponse: (response: any) => response?.data?.findOneUserGroupQuery ?? response?.findOneUserGroupQuery ?? null,
    }),

    studentCourseGroups: builder.query<{ items: GroupCourse[]; pageInfo: PageInfoType }, { studentId: string; page: number }>({
      query: ({ studentId, page }) => ({
        url: '', method: 'POST',
        body: JSON.stringify({
          query: `query StudentCourseGroups($filter: FilterFindManyUserGroupWithUsersInput!, $page: Int!, $perPage: Int!, $sort: SortFindManyUserGroupInput) {
            paginationUserGroupQuery(filter: $filter, page: $page, perPage: $perPage, sort: $sort) {
              items { _id name isRemoved users { user { _id } paymentTransactions { _id date source providerPaymentId status amount paidAmount debt lessons currency } remainingPaidLessons totalPaidAmount } data {
                course { _id } courseCategory { _id } lessonTemplate { _id title } teacher { _id firstName lastName }
              } }
              pageInfo { pageCount }
            }
          }`,
          variables: { filter: { users: [studentId] }, page, perPage: 100, sort: SORT_TYPE._ID_DESC },
        }),
      }),
      providesTags: ['userGroups'],
      transformResponse: (response: { data?: { paginationUserGroupQuery?: { items: GroupCourse[]; pageInfo: PageInfoType } } }) =>
        response.data?.paginationUserGroupQuery ?? { items: [], pageInfo: {} as PageInfoType },
    }),

    paginationUserGroup: builder.query<PaginationUserGroupResult, PaginationUserGroupArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'userGroup',
              fragment: userGroupFragment,
              filterType: 'FilterFindManyUserGroupWithUsersInput',
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['userGroups'],
      transformResponse: transformUserGroupResponse('paginationUserGroupQuery'),
    }),

    removeByIdUserGroup: builder.mutation<unknown, RemoveByIdUserGroupArgs>({
      invalidatesTags: ['userGroups'],
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdUserGroupMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findOneUserGroup: builder.query<unknown, FindOneUserGroupArgs>({
      providesTags: ['userGroups'],
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findOneUserGroupQuery),
          variables: {
            filter: args?.filter,
          },
        }),
      }),
    }),

    updateByIdUserGroup: builder.mutation<unknown, UpdateByIdUserGroupArgs>({
      invalidatesTags: ['userGroups'],
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdUserGroupMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    updateStudentLevelUserGroup: builder.mutation<unknown, UpdateStudentLevelUserGroupArgs>({
      invalidatesTags: ['userGroups'],
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateStudentLevelUserGroupMutation),
          variables: {
            groupId: args?.groupId,
            courseCategoryId: args?.courseCategoryId,
          },
        }),
      }),
    }),

    createOneUserGroup: builder.mutation<unknown, CreateOneUserGroupArgs>({
      invalidatesTags: ['userGroups'],
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneUserGroupMutation),
          variables: {
            type: args?.type,
            name: args?.name,
            teacher: args?.teacher,
            course: args?.course,
            courseCategory: args?.courseCategory,
            users: args?.users,
            memberPayments: args?.memberPayments,
          },
        }),
      }),
    }),
  };
};
